import { createFeatureSelector, createSelector } from '@ngrx/store';
import { get as _get } from 'lodash';

import { PartyPhone } from '@amfam/shared/models';

import { USER_FEATURE_KEY, UserState } from './user.reducer';

// Lookup the 'User' feature state managed by NgRx
const selectUserState = createFeatureSelector<UserState>(USER_FEATURE_KEY);

const selectBusinessName = createSelector(
  selectUserState,
  (state: UserState) => state.businessName
);
const selectUserLoading = createSelector(selectUserState, (state: UserState) => state.loading);
const selectUserLoaded = createSelector(selectUserState, (state: UserState) => state.loaded);

const selectCustomerId = createSelector(selectUserState, (state: UserState) => state.customerId);
const selectEmailAddress = createSelector(
  selectUserState,
  (state: UserState) => state.emailAddress
);
const selectExperienceId = createSelector(
  selectUserState,
  (state: UserState) => state.experienceId
);
const selectFirstName = createSelector(selectUserState, (state: UserState): string =>
  _get(state, 'party.firstName', _get(state, 'firstName', ''))
);
const selectLastName = createSelector(selectUserState, (state: UserState) =>
  _get(state, 'party.lastName', _get(state, 'lastName', ''))
);

export const selectGreetingName = createSelector(
  selectUserState,
  (state: UserState) => state.greetingName
);

const selectFirstAndLastName = createSelector(
  selectFirstName,
  selectLastName,
  (firstName, lastName) => {
    if (firstName || lastName) {
      return `${firstName} ${lastName}`;
    }
  }
);
const selectDisplayName = createSelector(selectUserState, (state: UserState) =>
  _get(state, 'party.displayName', _get(state, 'displayName', ''))
);
const selectLastLoggedInOn = createSelector(
  selectUserState,
  (state: UserState) => state.lastLoggedInOn
);
const selectLoginName = createSelector(selectUserState, (state: UserState) => state.loginName);
const selectPartnerId = createSelector(selectUserState, (state: UserState) => state.partnerId);
const selectSmImpersonator = createSelector(
  selectUserState,
  (state: UserState) => state.smImpersonator
);
const selectSmImpersonatorUserId = createSelector(
  selectUserState,
  (state: UserState) => state.smImpersonatorUserID
);
const selectTrackId = createSelector(selectUserState, (state: UserState) => state.trackId);
const selectTypeOfAccountCode = createSelector(
  selectUserState,
  (state: UserState) => state.typeOfAccountCode
);
const selectWaid = createSelector(selectUserState, (state: UserState) => state.waid);
const selectWaidMapped = createSelector(selectUserState, (state: UserState) => {
  const waid = state.waid;
  return !!waid;
});

const selectIsCustomer = createSelector(selectUserState, (state: UserState) => state.isCustomer);
const selectPartyDetails = createSelector(selectUserState, (state: UserState) => state.party);
export const selectUserStateCode = createSelector(selectPartyDetails, (party): string =>
  _get(party, 'addresses[0].state', '')
);
export const selectUserCity = createSelector(selectPartyDetails, (party): string =>
  _get(party, 'addresses[0].city', '')
);
export const selectPartyVersion = createSelector(selectPartyDetails, party =>
  _get(party, 'partyVersion', '')
);
export const selectCustomerIdAndLoginName = createSelector(
  selectCustomerId,
  selectLoginName,
  (customerId, loginName) => ({
    customerId,
    loginName
  })
);
const selectEmails = createSelector(selectUserState, (state: UserState) => state.party.emails);
const selectPhones = createSelector(selectUserState, (state: UserState) => state.party.phones);
const selectAddress = createSelector(selectUserState, (state: UserState) =>
  !state.party.addresses || !state.party.addresses.length
    ? undefined
    : state.party.addresses.find(address =>
        address.contactMethodUsages.some(usage => usage.typeOfResidencePurposeCode === 'PRIMARY')
      )
);

export const selectPhoneWithAreaCode = createSelector(selectPhones, phones =>
  phones.map(elem => elem.tenDigitPhoneNumber)
);

const selectMyAccountEmail = createSelector(selectUserState, (state: UserState) =>
  !state.party.emails || !state.party.emails.length
    ? undefined
    : state.party.emails.find(email =>
        email.contactMethodUsages.some(usage => usage.typeOfUsageCode === 'MYACCOUNT')
      )
);
const selectPrimaryEmail = createSelector(selectUserState, (state: UserState) =>
  !state.party.emails || !state.party.emails.length
    ? undefined
    : // if there is no primary phone return a placeholder email address
      state.party.emails.find(email => email.primaryIndicator) || undefined
);
const selectPrimaryPhone = createSelector(selectUserState, (state: UserState) =>
  !state.party.phones || !state.party.phones.length
    ? undefined
    : state.party.phones.find(email => email.primaryIndicator) || undefined
);

const selectPreEnrollementPhoneNumberObj = createSelector(selectUserState, (state: UserState) =>
  !state.party.phones || !state.party.phones.length
    ? undefined
    : state.party.phones.find(phone => phone.primaryIndicator) ||
      state.party.phones.find(phone => phone.contactMethodUsages[0].typeOfUsageCode === 'HOME') ||
      state.party.phones.find(phone => phone.contactMethodUsages[0].typeOfUsageCode === 'WORK')
);

const selectPreEnrollementPhoneNumber = createSelector(
  selectPreEnrollementPhoneNumberObj,
  (phone: PartyPhone) => {
    if (_get(phone, 'areaCode', '') !== '' && _get(phone, 'phoneNumber', '') !== '') {
      return _get(phone, 'areaCode') + _get(phone, 'phoneNumber');
    } else {
      return 'None';
    }
  }
);

const selectCustomerInfo = createSelector(
  selectCustomerId,
  selectFirstName,
  selectLastName,
  selectPrimaryEmail,
  selectMyAccountEmail,
  selectGreetingName,
  (customerId, firstname, lastname, primaryEmail, accountEmail, greetingName) => ({
    customerId,
    firstname,
    lastname,
    primaryEmail: _get(primaryEmail, 'emailAddress'),
    accountEmail: _get(accountEmail, 'emailAddress'),
    greetingName
  })
);

const selectZipCode = createSelector(selectAddress, address => address.zip5);

const selectCUSTKEY = createSelector(selectUserState, (state: UserState) => state.token);

const selectForgerockId = createSelector(selectUserState, state => state.forgerockId);

export const userQuery = {
  selectUserState,
  selectUserCity,
  selectBusinessName,
  selectCustomerId,
  selectEmailAddress,
  selectExperienceId,
  selectFirstName,
  selectGreetingName,
  selectLastName,
  selectFirstAndLastName,
  selectDisplayName,
  selectLastLoggedInOn,
  selectLoginName,
  selectPartnerId,
  selectSmImpersonator,
  selectSmImpersonatorUserId,
  selectTrackId,
  selectTypeOfAccountCode,
  selectWaid,
  selectWaidMapped,
  selectIsCustomer,
  selectPartyDetails,
  selectUserStateCode,
  selectPartyVersion,
  selectCustomerIdAndLoginName,
  selectEmails,
  selectPhones,
  selectPhoneWithAreaCode,
  selectAddress,
  selectMyAccountEmail,
  selectPrimaryEmail,
  selectPrimaryPhone,
  selectPreEnrollementPhoneNumber,
  selectUserLoading,
  selectUserLoaded,
  selectCustomerInfo,
  selectZipCode,
  selectCUSTKEY,
  selectForgerockId
};
